/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, Fragment } from 'react';

import moment from 'moment';

import Loader from 'components/Loader';
import PeriodSelect from 'components/PeriodSelect';
import Section from 'components/Section';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import SearchSelect, { RenderProps as SearchSelectRenderProps } from 'components/Selects/SearchSelct';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { AdminUser } from 'models/adminUser';
import { CostCenterReportProducts, CostCenterReportRequest } from 'models/costCenterReport';
import { Group } from 'models/group';
import { TravelReportRequest as Filters, TravelReportRequest } from 'models/reportSummary';
import { AccountResponse } from 'models/user';
import { DefaultCostCenterId } from 'screens/components/TravelReport/TravelReportFilter/constants/cost-centers';
import { extractErrorMessage } from 'utils/generic';

import { ReportFiltersContainer } from '../styles';
import OptionsItems from './components/OptionsItems';
import { Button } from './styles';

interface Props {
  loading: boolean;
  groupList: Group[];
  accountResult: AccountResponse | null;
  adminUserList: AdminUser[];
  fetchUsersAndInvites: (companyId: string) => Promise<void>;
  fetchGroupList: (companyId: string) => Promise<void>;
  fetchReportSummary: (companyId?: string, filters?: Filters) => Promise<void>;
  fetchCostCenterReport: (costCenterFilters: CostCenterReportRequest) => Promise<void>;
  filters?: TravelReportRequest;
  onSetPeriodText: (period?: string) => void;
}

const ReportForm = ({
  loading,
  accountResult,
  adminUserList,
  groupList,
  fetchGroupList,
  fetchUsersAndInvites,
  fetchReportSummary,
  onSetPeriodText,
  fetchCostCenterReport,
}: Props) => {
  const toast = useToast();
  const { t } = useStoreTranslation();

  const [isFetchingFiltersData, setIsFetchingFiltersData] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [selectedUser, setSelectedUser] = useState<AdminUser>();
  const [userList, setUserList] = useState<AdminUser[]>();
  const [filterStartDate, setFilterStartDate] = useState<string>();
  const [filterEndDate, setFilterEndDate] = useState<string>();
  const companyId = accountResult?.companyId;
  const groupRef = useRef(null);
  const userRef = useRef(null);

  const [labelReport, setLabelReport] = useState('');

  useEffect(() => {
    if (companyId && adminUserList.length === 0) {
      fetchUsersAndInvites(companyId)
        .then(() => {})
        .catch((error) => {
          toast.add({
            type: 'danger',
            message: extractErrorMessage(error) || t('defaultError.default'),
          });
        });
    }

    if (groupList.length === 0) {
      fetchGroupList(companyId || '')
        .then(() => {})
        .catch(() => {
          toast.add({
            type: 'danger',
            message: t('defaultError.default'),
          });
        });
    }
    setIsFetchingFiltersData(false);
  }, []);

  useEffect(() => {
    setUserList(adminUserList);
  }, [adminUserList]);

  const returnsUsersByGroupId = (id: string) => {
    const selectedGroupUsers = groupList.find((group) => group?.id === id);
    return selectedGroupUsers?.users;
  };

  const handleSelectGroup = (id: string) => {
    const group = groupList.find((g) => g.id === id);
    const usersByGroup = returnsUsersByGroupId(id);
    setUserList(usersByGroup);
    setSelectedGroup(group);
  };

  const transformUserNameOfGroupList = (group: AdminUser[], search: string) =>
    group.filter((user) => {
      const fullName = `${user.name} ${user.lastName}`.toLowerCase();
      return fullName.includes(search.toLowerCase());
    });

  const handleChangeUserName = (e: React.FormEvent<HTMLInputElement>) => {
    const inputedValue = e.currentTarget.value;
    setSearchText(inputedValue);

    if (!inputedValue) {
      setSelectedUser(undefined);
    }

    if (selectedGroup) {
      const usersSelected = returnsUsersByGroupId(selectedGroup.id);
      const selectedsUser = usersSelected && transformUserNameOfGroupList(usersSelected, inputedValue);
      if (selectedsUser) setUserList(selectedsUser);
      return;
    }

    const allUsers = transformUserNameOfGroupList(adminUserList, inputedValue);
    setUserList(allUsers);
  };

  const handleSelectUser = (id: string) => {
    const user = adminUserList.find((group) => group.id === id);
    setSearchText(`${user?.name} ${user?.lastName}`);
    setSelectedUser(user);
  };

  const onSeeResults = () => {
    const data: Filters = {
      FilterStartDate: filterStartDate,
      FilterEndDate: filterEndDate,
    };

    if (selectedUser) data.User = selectedUser || undefined;
    if (selectedGroup) data.Group = selectedGroup || undefined;

    onSetPeriodText(labelReport);
    fetchReportSummary(companyId, data);
    fetchCostCenterReport(getCostCenterReportFilters(data));
  };

  const getCostCenterReportFilters = (filter: Filters) =>
    ({
      initDate: filter.FilterStartDate,
      endDate: filter.FilterEndDate,
      tripType: CostCenterReportProducts.All,
      costCenterId: DefaultCostCenterId,
      userGroupId: filter.Group?.id ?? undefined,
    } as CostCenterReportRequest);

  const isValidUser = () => {
    if (searchText || selectedUser) {
      const condition = searchText.toLowerCase() === `${selectedUser?.name} ${selectedUser?.lastName}`.toLowerCase();

      if (!condition) return t('travelReport.filterForm.invalidUser');
    }
  };

  const changeDateFilters = (start: string, end: string, value: string) => {
    setFilterEndDate(end);
    setFilterStartDate(start);

    let label = '';
    if (value === 'date') {
      label = `${moment(start).format('DD MMM')} - ${moment(end).format('DD MMM')}`;
    }
    if (value === 'thirtyDays') {
      label = t('periodSelect.options.lastDays');
    }
    if (value === 'previous') {
      label = t('periodSelect.options.previousMonth', {
        month: moment().subtract(1, 'months').endOf('month').format('MMMM'),
      });
    }
    if (value === 'current') {
      label = t('periodSelect.options.currentMonth', { month: moment().format('MMMM') });
    }

    setLabelReport(label);
  };

  return (
    <Fragment>
      {isFetchingFiltersData ? (
        <Loader fullScreen />
      ) : (
        <ReportFiltersContainer>
          <Section title={t('travelReport.filterForm.title')}>
            <PeriodSelect label={t('travelReport.filterForm.selectPeriod')} handleChangeDate={changeDateFilters} />
            <CustomSelect
              name="group"
              onChange={handleSelectGroup}
              label={t('travelReport.filterForm.selectGroup')}
              placeHolder={t('travelReport.filterForm.selectGroup')}
              selectRef={groupRef}
              value={selectedGroup}
            >
              {({ ...selectProps }: CustomSelectRenderProps) => (
                <OptionsItems {...selectProps} selectRef={groupRef} groups={groupList} />
              )}
            </CustomSelect>
            <SearchSelect
              name="user"
              error={isValidUser()}
              onChange={handleChangeUserName}
              onChildrenChange={handleSelectUser}
              label={t('travelReport.filterForm.selectUser')}
              placeHolder={t('travelReport.filterForm.userPlaceHolder')}
              selectRef={userRef}
              value={searchText}
            >
              {({ ...selectProps }: SearchSelectRenderProps) => (
                <OptionsItems {...selectProps} selectRef={userRef} users={userList} />
              )}
            </SearchSelect>
            <Button
              invertedColor
              disabled={loading || (!filterStartDate && !filterEndDate)}
              onClick={onSeeResults}
              value={t('travelReport.search')}
            />
          </Section>
        </ReportFiltersContainer>
      )}
    </Fragment>
  );
};

export default ReportForm;
